import {
  Input,
  Form,
  Select,
  Card,
  message,
  Button,
  DatePicker,
  Skeleton,
  Table,
  Popconfirm,
  Typography,
  Switch,
  Checkbox,
  Flex,
  Pagination,
  Alert,
} from 'antd';
import UserDoc from '../../../domain/models/UserDoc';
import { FormCard, FormContainer, FormSectionTitle, READ_ONLY_INPUT_STYLE } from '@leedpulse-monorepo/components';
import {
  UserRole,
  userRoleAsString,
  UserType,
  userTypeAsString,
  ContextStatus,
  EProductType,
  UserCompanyType,
  userCompanyTypeAsString,
  EPhoneDisplayType,
  phoneDisplayTypeAsString,
  mailTypeAsString,
  mailTypeToHistoryEntryName,
} from '@leedpulse-monorepo/enums';
import { countryCodes, isValidPhoneNumber } from '@leedpulse-monorepo/utils';
import { useContext, useEffect, useMemo, useState } from 'react';
import UserEditionFormContext, {
  USER_SUBSCRIPTIONS_PAGE_SIZE,
} from '../../contexts/forms/users/UserEditionFormContext';
import UsersContext from '../../contexts/data/UsersContext';
import { USERS_PAGE_PATH } from '../../pages/users/UsersPage';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import AvatarInput from '../@shared/AvatarInput';
import UserConnectors from './UserConnectors';
import UserAppPermissions from './UserAppPermissions';
import ConnectSepteoModal from './user_details/ConnectSepteoModal';
import ConnectApimoModal from './user_details/ConnectApimoModal';
import ChangeUserEmailModal, { EmailField } from './user_details/ChangeUserEmailModal';
import {
  SyncOutlined,
  CheckOutlined,
  ArrowRightOutlined,
  ToolOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  MobileOutlined,
  DesktopOutlined,
  ClockCircleOutlined,
  BankOutlined,
  CalendarOutlined,
  MailOutlined,
  SwapOutlined,
  LockOutlined,
  PhoneOutlined,
  ApiOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import '../user_detail_page/user_details/UserSubscriptionInput.css';
import moment from 'moment';
import {
  handleErrorInputMessage,
  handleInputError,
} from '../../../core/utils/errorUtils';
import SubscriptionsContext from '../../contexts/data/SubscriptionsContext';
import Modal from 'antd/es/modal/Modal';
import { useNavigate } from 'react-router-dom';
import Subscription from '../../../domain/models/Subscription';
import revokeRefreshTokens from '../../../domain/use_cases/users/revokeRefreshTokens';
import PlansContext from '../../contexts/data/PlansContext';
import AvailabilityHoursModal from './user_details/AvailabilityHoursModal';
import { DAYS_OF_WEEK, DAY_LABELS, formatSlotsSummary } from '../../../domain/models/AvailabilityHours';
import { ICompany } from '@leedpulse-monorepo/interfaces';
import { advancedOnRow } from '../../../core/utils/commonUtils';
import { COMPANIES_PAGE_PATH } from '../../pages/companies/CompaniesPage';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionsPage';
import sendMail from '../../../domain/use_cases/mails/sendMail';
import previewMail, { PreviewMailResponse } from '../../../domain/use_cases/mails/previewMail';
import getLastUserHistoryDate from '../../../domain/use_cases/users/getLastUserHistoryDate';
import { EMailType } from '@leedpulse-monorepo/enums';
import {
  getUserApiKey,
  generateUserApiKey,
  revokeUserApiKey,
} from '../../../domain/use_cases/users/manageUserApiKey';
import { LeedflowLogoMinimalist, LeedpulseLogoMinimalist } from '@leedpulse-monorepo/assets';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';

interface UserDetailsProps {
  user: UserDoc;
}

const UserDetails: React.FC<UserDetailsProps> = ({ user }) => {
  const navigate = useNavigate();
  const userEditionFormContext = useContext(UserEditionFormContext);
  const usersContext = useContext(UsersContext);
  const subscriptionsContext = useContext(SubscriptionsContext);
  const authContext = useContext(AuthenticationContext);

  const plansContext = useContext(PlansContext);

  const [recipientTransferSelected, setRecipientTransferSelected] =
    useState(null);
  const [scenariosTransferSelected, setScenariosTransferSelected] = useState(
    []
  );
  const [selectedMailType, setSelectedMailType] = useState<EMailType>(EMailType.WELCOME_LEEDFLOW);
  const [lastSentMails, setLastSentMails] = useState<Partial<Record<EMailType, number | null>>>();
  // Every manual send goes through a preview: choosing between mails on a label alone is how the wrong one leaves
  const [mailPreview, setMailPreview] = useState<PreviewMailResponse | null>(null);
  const [isMailBusy, setIsMailBusy] = useState(false);

  const [isChangePasswordModalOpened, setIsChangePasswordModalOpened] =
    useState(false);
  const [isAvailabilityModalOpened, setIsAvailabilityModalOpened] =
    useState(false);
  const [isConnectSepteoModalOpened, setIsConnectSepteoModalOpened] =
    useState(false);
  const [isConnectApimoModalOpened, setIsConnectApimoModalOpened] =
    useState(false);
  const [emailFieldToEdit, setEmailFieldToEdit] = useState<EmailField | null>(
    null
  );

  const [apiKey, setApiKey] = useState<string | null>(null);
  const [apiKeyRevealed, setApiKeyRevealed] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [revokeApiKeyModalVisible, setRevokeApiKeyModalVisible] = useState(false);

  const isBusy =
    userEditionFormContext.updateStatus === ContextStatus.LOADING ||
    userEditionFormContext.deleteStatus === ContextStatus.LOADING;

  const isStaff = [UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
    authContext.authentication?.role ?? UserRole.guest
  );

  const getAvailableRoles = () => {
    return [
      UserRole.owner,
      UserRole.admin,
      UserRole.maintainer,
      UserRole.guest,
      UserRole.customer,
    ];
  };

  const wipUser = userEditionFormContext.wipUser;
  const companies = userEditionFormContext.companies;
  const subscriptions = userEditionFormContext.subscriptions;

  const isLeedflowUser = useMemo(() => {
    if (!wipUser) return false;
    return Object.keys(wipUser.idSubscription).some((subId) => {
      const idPlan =
        userEditionFormContext.subscriptionMetaById[subId]?.idPlan ??
        subscriptions.find((sub) => sub.id === subId)?.idPlan;
      if (!idPlan) return false;
      return (
        plansContext.items.find((p) => p.id === idPlan)?.product ===
        EProductType.LEEDFLOW
      );
    });
  }, [
    wipUser,
    userEditionFormContext.subscriptionMetaById,
    subscriptions,
    plansContext.items,
  ]);

  const currentEditionRoles = getAvailableRoles();

  const handleSendMail = async (mailType: EMailType) => {
    try {
      if (!wipUser || !authContext.authentication) return;
      await sendMail(mailType, wipUser.uid, authContext.authentication);

      message.success(`${mailTypeAsString(mailType)} envoyé avec succès`);
      if (mailTypeToHistoryEntryName(mailType)) {
        setLastSentMails((o) => ({ ...o, [mailType]: Date.now() }));
      }
    } catch (error) {
      message.error("Erreur lors de l'envoi du mail");
    }
  };

  const handlePreviewMail = async (mailType: EMailType) => {
    if (!wipUser || !authContext.authentication) return;
    setIsMailBusy(true);
    try {
      setMailPreview(await previewMail(mailType, wipUser.uid, authContext.authentication));
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Aperçu du mail indisponible");
    }
    setIsMailBusy(false);
  };

  const handleConfirmSendMail = async () => {
    setIsMailBusy(true);
    await handleSendMail(selectedMailType);
    setIsMailBusy(false);
    setMailPreview(null);
  };

  const handleGenerateApiKey = async () => {
    if (!wipUser || !authContext.authentication) return;
    setApiKeyLoading(true);
    try {
      const key = await generateUserApiKey(wipUser.uid, authContext.authentication);
      setApiKey(key);
      setApiKeyRevealed(true);
      message.success('Clé API générée avec succès. Copiez-la maintenant, elle ne sera plus affichée.');
    } catch (error) {
      message.error('Erreur lors de la génération de la clé API');
    }
    setApiKeyLoading(false);
  };

  const handleRevokeApiKey = async () => {
    if (!wipUser || !authContext.authentication) return;
    setApiKeyLoading(true);
    try {
      await revokeUserApiKey(wipUser.uid, authContext.authentication);
      setApiKey(null);
      setApiKeyRevealed(false);
      setRevokeApiKeyModalVisible(false);
      message.success('Clé API révoquée avec succès.');
    } catch (error) {
      message.error('Erreur lors de la révocation de la clé API');
    }
    setApiKeyLoading(false);
  };

  const handleCopyApiKey = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      message.success('Clé API copiée dans le presse-papier');
    }
  };

  useEffect(() => {
    if (userEditionFormContext.wipUser === null) {
      userEditionFormContext.initWithUser(user);
    } else {
      subscriptionsContext.fetchAllUsers(
        userEditionFormContext.wipUser.idSubscription
      );
      userEditionFormContext.fetchScenarios(userEditionFormContext.wipUser.uid);
    }
  }, [userEditionFormContext.wipUser?.uid]);

  useEffect(() => {
    const uid = wipUser?.uid;
    if (!uid) return;
    Object.values(EMailType).forEach((mailType) => {
      const historyName = mailTypeToHistoryEntryName(mailType);
      if (!historyName) return;
      getLastUserHistoryDate(uid, historyName)
        .then((lastSent) => setLastSentMails((o) => ({ ...o, [mailType]: lastSent })))
        .catch(() => setLastSentMails((o) => ({ ...o, [mailType]: null })));
    });
  }, [wipUser?.uid]);

  useEffect(() => {
    const uid = wipUser?.uid;
    if (!uid || !authContext.authentication) return;
    // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
    // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
    // external system") ; le setState synchrone est le reset qui precede l'appel.
    // eslint-disable-next-line react/set-state-in-effect
    setApiKey(null);
    setApiKeyRevealed(false);
    getUserApiKey(uid, authContext.authentication)
      .then((state) => setApiKey(state.apiKey))
      .catch(() => setApiKey(null));
  }, [wipUser?.uid, authContext.authentication]);

  useEffect(() => {
    switch (userEditionFormContext.updateStatus) {
      case ContextStatus.SUCCESS:
        message.success("Mise à jour de l'utilisateur avec succès");
        break;
      case ContextStatus.ERROR:
        message.error(userEditionFormContext.errorMessage);
        break;
      default:
        break;
    }
  }, [userEditionFormContext.updateStatus]);

  useEffect(() => {
    switch (userEditionFormContext.deleteStatus) {
      case ContextStatus.SUCCESS:
        usersContext.fetch();
        message.success('Utilisateur supprimé avec succès');
        navigate(USERS_PAGE_PATH);
        break;
      case ContextStatus.ERROR:
        message.error("Erreur lors de la suppression de l'utilisateur");
        break;
      default:
        break;
    }
  }, [userEditionFormContext.deleteStatus]);

  return wipUser === null ? (
    <Card loading />
  ) : (
    <>
      <FormCard
        title={wipUser.uid}
        collectionName={FirestoreCollections.users}
        onDeleted={
          () => userEditionFormContext.submitDelete()
        }
        onUpdated={
          () => userEditionFormContext.submitUpdate()
        }
        isUpdateLoading={isBusy}
        isDeleteLoading={isBusy}
        headerActions={[
          <Button
            type="primary"
            key="login-as-leedflow"
            onClick={() => usersContext.loginAsUser(wipUser, 'computer', EProductType.LEEDFLOW)}
            icon={
              <Flex align='center' justify='center'>
                <img src={LeedflowLogoMinimalist} alt={EProductType.LEEDFLOW} style={{ height: '18px' }} />
              </Flex>
            }
            loading={isBusy}
            disabled={isBusy}>
            <span className="extra-label">Leedflow (web)</span>
          </Button>,
          <Button
            type="primary"
            key="login-as-leedpulse"
            onClick={() => usersContext.loginAsUser(wipUser, 'computer', EProductType.LEEDPULSE)}
            icon={
              <Flex align='center' justify='center'>
                <img src={LeedpulseLogoMinimalist} alt={EProductType.LEEDPULSE} style={{ height: '18px' }} />
              </Flex>
            }
            loading={isBusy}
            disabled={isBusy}>
            <span className="extra-label">Leedpulse (web)</span>
          </Button>,
          <Button
            type="primary"
            key="login-as-leedflow-mobile"
            onClick={() => usersContext.loginAsUser(wipUser, 'mobile', EProductType.LEEDFLOW)}
            icon={<MobileOutlined />}
            loading={isBusy}
            disabled={isBusy}>
            <span className="extra-label">Leedflow (mobile)</span>
          </Button>,
        ]}
      >
        <Form
          layout="vertical"
          initialValues={{ remember: true }}
          requiredMark={false}
        >
          <Form.Item label="Notes">
            <Input.TextArea
              disabled={isBusy}
              rows={3}
              placeholder="Notes internes sur cet utilisateur (contexte, historique, points d'attention...)"
              value={wipUser.notes}
              onChange={(e) => {
                userEditionFormContext.onNotesChange(e.target.value);
              }}
            />
          </Form.Item>

          <FormSectionTitle title="ℹ️ Informations générales" />
          <FormContainer twoColumnsMax>
            <Form.Item label="Identifiant Leed">
              <Input
                type="text"
                disabled={true}
                readOnly={true}
                style={READ_ONLY_INPUT_STYLE}
                value={wipUser.id}
              />
            </Form.Item>
            {wipUser.partnerUid && (
              <Form.Item label="Identifiant partenaire">
                <Input
                  type="text"
                  disabled={true}
                  readOnly={true}
                  style={READ_ONLY_INPUT_STYLE}
                  value={
                    wipUser.partnerUid + ' (' + wipUser.partnerSystem + ')'
                  }
                />
              </Form.Item>
            )}
            <Form.Item label="Personne morale">
              <Checkbox
                checked={wipUser.legalEntity}
                onChange={(e) => {
                  userEditionFormContext.onLegalEntityChange(e.target.checked);
                }}
              />
            </Form.Item>
          </FormContainer>

          <FormContainer>
            <Form.Item label="Avatar">
              <AvatarInput
                disabled={isBusy}
                initialBase64={wipUser.avatar}
                onChange={(base64) => {
                  userEditionFormContext.onAvatarChange(base64);
                }}
              />
            </Form.Item>
            <Form.Item
              label="Prénom"
              validateStatus={handleInputError(
                wipUser.firstname,
                userEditionFormContext.status
              )}
              help={handleErrorInputMessage(
                wipUser.firstname,
                userEditionFormContext.status
              )}
            >
              <Input
                disabled={isBusy}
                onChange={(e) => {
                  userEditionFormContext.onFirstnameChange(e.target.value);
                }}
                value={wipUser.firstname}
              />
            </Form.Item>
            <Form.Item
              label="Nom"
              validateStatus={handleInputError(
                wipUser.lastname,
                userEditionFormContext.status
              )}
              help={handleErrorInputMessage(
                wipUser.lastname,
                userEditionFormContext.status
              )}
            >
              <Input
                disabled={isBusy}
                onChange={(e) => {
                  userEditionFormContext.onLastnameChange(e.target.value);
                }}
                value={wipUser.lastname}
              />
            </Form.Item>
            <Form.Item
              label={
                <Flex align="center" gap={4}>
                  Email
                  {isStaff && (
                    <Button
                      type="text"
                      shape="circle"
                      size="small"
                      icon={<EditOutlined />}
                      title="Modifier l'email"
                      disabled={isBusy}
                      onClick={() => setEmailFieldToEdit('email')}
                    />
                  )}
                </Flex>
              }
            >
              <Input
                disabled={isBusy}
                readOnly={true}
                style={READ_ONLY_INPUT_STYLE}
                value={wipUser.email}
              />
            </Form.Item>
            <Form.Item
              label={
                <Flex align="center" gap={4}>
                  Email d&apos;authentification
                  {isStaff && (
                    <Button
                      type="text"
                      shape="circle"
                      size="small"
                      icon={<EditOutlined />}
                      title="Modifier l'email d'authentification"
                      disabled={isBusy}
                      onClick={() => setEmailFieldToEdit('emailAuthentication')}
                    />
                  )}
                </Flex>
              }
            >
              <Input
                disabled={isBusy}
                readOnly={true}
                style={READ_ONLY_INPUT_STYLE}
                value={wipUser.emailAuthentication}
              />
            </Form.Item>
            <Form.Item
              label="Téléphone"
              validateStatus={handleInputError(
                wipUser.phone,
                userEditionFormContext.status
              )}
              help={handleErrorInputMessage(
                wipUser.phone,
                userEditionFormContext.status
              )}
            >
              <Input
                disabled={isBusy}
                value={userEditionFormContext.phoneNumber}
                onChange={(e) =>
                  userEditionFormContext.onPhoneNumberChange(e.target.value)
                }
                maxLength={userEditionFormContext.selectedCountry.digitsLength}
                allowClear
                placeholder={`Numéro (${userEditionFormContext.selectedCountry.digitsLength} chiffres)`}
                addonBefore={
                  <Select
                    value={userEditionFormContext.selectedCountry.name}
                    style={{ width: 100 }}
                    onChange={(value) => {
                      const country = countryCodes.find(
                        (c) => c.name === value
                      );
                      if (country) {
                        userEditionFormContext.onSelectedCountryCodeChange(
                          country
                        );
                      }
                    }}
                    popupMatchSelectWidth={false}
                    disabled={isBusy}
                    showSearch
                    filterOption={(input, option) => {
                      const childText = option?.children?.toString() || '';
                      return (
                        childText.toLowerCase().indexOf(input.toLowerCase()) >=
                        0
                      );
                    }}
                  >
                    {countryCodes.map((country) => (
                      <Select.Option key={country.name} value={country.name}>
                        {country.flag}{' '}
                        {country.shortName || country.name.substring(0, 3)}
                      </Select.Option>
                    ))}
                  </Select>
                }
                status={
                  userEditionFormContext.phoneNumber &&
                    !isValidPhoneNumber(
                      userEditionFormContext.phoneNumber,
                      userEditionFormContext.selectedCountry.digitsLength
                    )
                    ? 'error'
                    : undefined
                }
                suffix={
                  userEditionFormContext.phoneNumber &&
                    isValidPhoneNumber(
                      userEditionFormContext.phoneNumber,
                      userEditionFormContext.selectedCountry.digitsLength
                    ) ? (
                    <CheckOutlined style={{ color: 'green' }} />
                  ) : undefined
                }
                showCount
              />
            </Form.Item>

            <Form.Item label="Rôle">
              <Select
                disabled={isBusy}
                onChange={(value) => {
                  userEditionFormContext.onRoleChange(value as UserRole);
                }}
                value={wipUser.role}
              >
                {currentEditionRoles.map((key) => (
                  <Select.Option
                    key={key}
                    value={UserRole[key as keyof typeof UserRole]}
                  >
                    {userRoleAsString(key)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Intitulé de poste"
              validateStatus={handleInputError(
                wipUser.position,
                userEditionFormContext.status
              )}
              help={handleErrorInputMessage(
                wipUser.position,
                userEditionFormContext.status
              )}
            >
              <Input
                disabled={isBusy}
                onChange={(e) => {
                  userEditionFormContext.onPositionChange(e.target.value);
                }}
                value={wipUser.position}
                placeholder="Conseiller(e) immobilier"
              />
            </Form.Item>
          </FormContainer>

          <FormSectionTitle title="☎️ Téléphonie" />
          <FormContainer twoColumnsMax>
            <Form.Item label={<Flex align="center" ><PhoneOutlined style={{ marginRight: 6 }} />N° à afficher (appels sortants)</Flex>} extra="phone_display_type">
              <Select
                disabled={isBusy}
                value={wipUser.phoneDisplayType}
                options={Object.values(EPhoneDisplayType).map((type) => ({
                  label: phoneDisplayTypeAsString(type),
                  value: type,
                }))}
                onChange={(value) => {
                  userEditionFormContext.onPhoneDisplayTypeChange(value as EPhoneDisplayType);
                }}
              />
            </Form.Item>
            <Form.Item label="Messagerie vocale">
              <Input.TextArea
                disabled={isBusy}
                maxLength={200}
                showCount
                rows={3}
                onChange={(e) => {
                  userEditionFormContext.onVoicemailTxtToSpeechChange(e.target.value);
                }}
                value={wipUser.voicemailTxtToSpeech}
                placeholder="Je ne suis pas disponible, laissez-moi un message et je vous rappellerai rapidement."
              />
            </Form.Item>
          </FormContainer>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--surface-color)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              width: '100%',
              marginBottom: '32px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography.Text strong style={{ fontSize: '15px' }}>
                VoIP
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
                Active le routage VoIP pour cet utilisateur dans les réponses Callr.
              </Typography.Text>
            </div>
            <Switch
              disabled={isBusy}
              checked={wipUser.voip_enabled}
              onChange={() => userEditionFormContext.onVoipToggle()}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--surface-color)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              width: '100%',
              marginBottom: '32px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography.Text strong style={{ fontSize: '15px' }}>
                Annonce d&apos;enregistrement légal
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
                Joue une annonce légale au début de chaque appel entrant pour informer le correspondant de l&apos;enregistrement.
              </Typography.Text>
            </div>
            <Switch
              disabled={isBusy}
              checked={wipUser.recordingAnnouncementEnabled}
              onChange={() => userEditionFormContext.onRecordingAnnouncementToggle()}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--surface-color)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              width: '100%',
              marginBottom: '32px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography.Text strong style={{ fontSize: '15px' }}>
                Synchroniser les tâches avec Google Agenda
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
                Les tâches Leedflow seront automatiquement ajoutées au Google Agenda de l&apos;utilisateur.
              </Typography.Text>
            </div>
            <Switch
              disabled={isBusy}
              checked={wipUser.calendarTasksCreationEnabled}
              onChange={() => userEditionFormContext.onCalendarTasksCreationToggle()}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--surface-color)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              width: '100%',
              marginBottom: '32px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography.Text strong style={{ fontSize: '15px' }}>
                Bloquer les SMS
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
                Bloque la possibilité de rédiger et envoyer des SMS in-app pour cet utilisateur.
              </Typography.Text>
            </div>
            <Switch
              disabled={isBusy}
              checked={wipUser.smsDisabled}
              onChange={() => userEditionFormContext.onSmsDisabledToggle()}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--surface-color)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              width: '100%',
              marginBottom: '32px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography.Text strong style={{ fontSize: '15px' }}>
                Ne pas déranger
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
                Renvoie les appels entrants vers la messagerie et coupe les notifications push.
              </Typography.Text>
            </div>
            <Switch
              disabled={isBusy}
              checked={wipUser.doNotDisturb}
              onChange={() => userEditionFormContext.onDoNotDisturbToggle()}
            />
          </div>

          {isLeedflowUser && (
            <>
              <FormSectionTitle title={<Flex align="center"><ClockCircleOutlined style={{ marginRight: 6 }} />Horaires de disponibilité</Flex>} />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: 'var(--surface-color)',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  width: '100%',
                  marginBottom: wipUser.availabilityHours?.enabled ? 0 : '32px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography.Text strong style={{ fontSize: '15px' }}>
                    Activer les horaires
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
                    {wipUser.availabilityHours?.enabled
                      ? 'Les horaires de disponibilité sont activés.'
                      : 'En dehors des créneaux définis, les clients seront redirigés vers la messagerie.'}
                  </Typography.Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {wipUser.availabilityHours?.enabled && (
                    <Button
                      type="default"
                      icon={<EditOutlined />}
                      onClick={() => setIsAvailabilityModalOpened(true)}
                      disabled={isBusy}
                    >
                      Modifier
                    </Button>
                  )}
                  <Switch
                    disabled={isBusy}
                    checked={wipUser.availabilityHours?.enabled ?? false}
                    onChange={() => userEditionFormContext.onAvailabilityHoursToggle()}
                  />
                </div>
              </div>
              {wipUser.availabilityHours?.enabled && (
                <div
                  style={{
                    marginTop: '16px',
                    marginBottom: '32px',
                    padding: '0',
                    backgroundColor: 'var(--surface-color)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      width: '100%',
                    }}
                  >
                    {DAYS_OF_WEEK.map((day, index) => {
                      const daySchedule = wipUser.availabilityHours?.schedule[day];
                      if (!daySchedule) return null;
                      const isLastRow = index >= DAYS_OF_WEEK.length - 2;
                      const isLeftColumn = index % 2 === 0;

                      return (
                        <div
                          key={day}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '14px 24px',
                            backgroundColor: daySchedule.enabled ? 'var(--surface-color)' : 'var(--background-color)',
                            borderBottom: isLastRow ? 'none' : '1px solid var(--border-color)',
                            borderRight: isLeftColumn ? '1px solid var(--border-color)' : 'none',
                          }}
                        >
                          <Typography.Text
                            style={{
                              fontWeight: 500,
                              color: daySchedule.enabled ? '#262626' : '#bfbfbf',
                              fontSize: '15px',
                            }}
                          >
                            {DAY_LABELS[day]}
                          </Typography.Text>
                          <Typography.Text
                            style={{
                              color: daySchedule.enabled ? 'var(--text-base-color)' : 'var(--text-secondary-color)',
                              fontSize: '14px',
                              fontWeight: daySchedule.enabled ? 500 : 400,
                            }}
                          >
                            {!daySchedule.enabled ? 'Fermé' : formatSlotsSummary(daySchedule)}
                          </Typography.Text>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          <FormSectionTitle
            title={<Flex align="center"><BankOutlined style={{ marginRight: 6 }} />Entreprises ({companies.length})</Flex>}
          />
          <div className="users-card-companies-container">
            {userEditionFormContext.status === ContextStatus.LOADING ? (
              <Skeleton active />
            ) : userEditionFormContext.status === ContextStatus.SUCCESS &&
              companies.length > 0 ? (
              <Table
                pagination={false}
                showHeader={false}
                columns={[
                  {
                    title: 'Nom',
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: 'Droits',
                    key: 'roles',
                    render: (record: ICompany) =>
                      <Select
                        style={{ width: '350px' }}
                        disabled={isBusy}
                        onChange={(value) => {
                          if (value.length === 0) {
                            message.error('Veuillez sélectionner au moins un rôle.');
                            return;
                          }
                          userEditionFormContext.onIdCompaniesChange({ [record.id]: { roles: value as UserType[], updated: new Date().getTime() } });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        value={wipUser.idCompanies[record.id]?.roles}
                        mode="multiple"
                        allowClear={false}
                      >
                        {Object.values(UserCompanyType).map((key) => (
                          <Select.Option
                            key={key}
                            value={key}
                          >
                            {userCompanyTypeAsString(key)}
                          </Select.Option>
                        ))}
                      </Select>
                  },
                  {
                    title: 'Action',
                    render: () => <button type="button" style={{ background: 'none', border: 'none', padding: 0, color: '#1677ff', cursor: 'pointer' }}>Voir le détail</button>,
                  },
                ]}
                rowKey={(record) => record.id}
                dataSource={companies}
                onRow={(record) =>
                  advancedOnRow({
                    url: `${COMPANIES_PAGE_PATH}/${record.id}`,
                    navigate,
                    className: 'table-row',
                  })
                }
              />
            ) : userEditionFormContext.status === ContextStatus.SUCCESS ? (
              <p
                style={{ textAlign: 'center', fontSize: '14px', color: '#888' }}
              >
                Aucune entreprise disponible
              </p>
            ) : (
              <p
                style={{ textAlign: 'center', fontSize: '14px', color: 'red' }}
              >
                Erreur lors du chargement des abonnements
              </p>
            )}
          </div>

          <FormSectionTitle
            title={
              <Flex align="center" justify="flex-start" gap={10}>
                <Flex align="center" gap={6}>
                  <CalendarOutlined /> Abonnements ({userEditionFormContext.subscriptionsTotalCount})
                </Flex>
                <Input
                  placeholder="Rechercher un abonnement"
                  style={{ width: '18.75rem' }}
                  value={userEditionFormContext.subscriptionSearchDraft}
                  onChange={(e) =>
                    userEditionFormContext.setSubscriptionSearchDraft(e.target.value)
                  }
                  allowClear
                />
                <Pagination
                  current={userEditionFormContext.subscriptionsTablePage}
                  pageSize={USER_SUBSCRIPTIONS_PAGE_SIZE}
                  showSizeChanger={false}
                  total={userEditionFormContext.subscriptionsTotalCount}
                  style={{ marginLeft: 'auto' }}
                  onChange={(page) =>
                    void userEditionFormContext.goToSubscriptionsPage(page)
                  }
                />
              </Flex>
            }
          />
          <div className="users-card-subscription-container">
            {userEditionFormContext.subscriptionsListStatus === ContextStatus.LOADING ? (
              <Skeleton active />
            ) : userEditionFormContext.subscriptionsListStatus === ContextStatus.SUCCESS &&
              userEditionFormContext.subscriptionsTotalCount > 0 ? (
              <Table
                pagination={{
                  current: userEditionFormContext.subscriptionsTablePage,
                  pageSize: USER_SUBSCRIPTIONS_PAGE_SIZE,
                  total: userEditionFormContext.subscriptionsTotalCount,
                  showSizeChanger: false,
                  position: ['bottomRight'],
                  onChange: (page) =>
                    void userEditionFormContext.goToSubscriptionsPage(page),
                }}
                showHeader={false}
                columns={[
                  {
                    title: 'Nom',
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: 'Droits',
                    key: 'roles',
                    render: (record: Subscription) =>
                      <Select
                        style={{ width: '350px' }}
                        disabled={isBusy}
                        onChange={(value) => {
                          if (value.length === 0) {
                            message.error('Veuillez sélectionner au moins un rôle.');
                            return;
                          }
                          userEditionFormContext.onIdSubscriptionChange({ [record.id]: { roles: value as UserType[] } });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        value={wipUser.idSubscription[record.id]?.roles}
                        mode="multiple"
                        allowClear={false}
                      >
                        {Object.values(UserType).map((key) => (
                          <Select.Option
                            key={key}
                            value={key}
                          >
                            {userTypeAsString(key)}
                          </Select.Option>
                        ))}
                      </Select>
                  },
                  {
                    title: 'Action',
                    render: () => <button type="button" style={{ background: 'none', border: 'none', padding: 0, color: '#1677ff', cursor: 'pointer' }}>Voir le détail</button>,
                  },
                ]}
                rowKey={(record) => record.id}
                dataSource={subscriptions}
                onRow={(record) =>
                  advancedOnRow({
                    url: `${SUBSCRIPTIONS_PAGE_PATH}/${record.id}`,
                    navigate,
                    className: 'table-row',
                  })
                }
              />
            ) : userEditionFormContext.subscriptionsListStatus === ContextStatus.SUCCESS ? (
              <p
                style={{ textAlign: 'center', fontSize: '14px', color: '#888' }}
              >
                Aucun abonnement disponible
              </p>
            ) : (
              <p
                style={{ textAlign: 'center', fontSize: '14px', color: 'red' }}
              >
                Erreur lors du chargement des abonnements
              </p>
            )}
          </div>

          <UserConnectors connectors={wipUser.connectors} />

          <Flex gap={8} style={{ marginBottom: '32px' }}>
            <Button
              icon={<ApiOutlined />}
              onClick={() => setIsConnectSepteoModalOpened(true)}
            >
              Connecter Septeo
            </Button>

            <Button
              icon={<ApiOutlined />}
              onClick={() => setIsConnectApimoModalOpened(true)}
            >
              Connecter Apimo
            </Button>
          </Flex>

          {isConnectSepteoModalOpened && (
            <ConnectSepteoModal
              open={isConnectSepteoModalOpened}
              onClose={() => setIsConnectSepteoModalOpened(false)}
              userId={wipUser.uid}
              companies={companies}
              subscriptions={subscriptions}
              leedflowPlanIds={plansContext.items
                .filter((p) => p.product === EProductType.LEEDFLOW)
                .map((p) => p.id)}
              auth={authContext.authentication}
              onSuccess={() => window.location.reload()}
            />
          )}

          {isConnectApimoModalOpened && (
            <ConnectApimoModal
              open={isConnectApimoModalOpened}
              onClose={() => setIsConnectApimoModalOpened(false)}
              userId={wipUser.uid}
              userEmails={[wipUser.email, wipUser.emailAuthentication]}
              companies={companies}
              auth={authContext.authentication}
              onSuccess={() => window.location.reload()}
            />
          )}

          <FormSectionTitle title={<Flex align="center"><CodeOutlined style={{ marginRight: 6 }} />API</Flex>} />
          <div style={{ marginBottom: '32px' }}>
            <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: 16 }}>
              Permet à cet utilisateur d'accéder à ses contacts, appels, SMS, messages vocaux et tâches via l'API REST.
            </Typography.Text>

            {!apiKey && (
              <Button
                type="primary"
                icon={<ApiOutlined />}
                loading={apiKeyLoading}
                disabled={isBusy}
                onClick={handleGenerateApiKey}
              >
                Générer une clé API
              </Button>
            )}

            {apiKey && (
              <div>
                <div
                  style={{
                    background: 'var(--surface-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 6,
                    padding: '12px 14px',
                    marginBottom: 12,
                    fontFamily: 'monospace',
                    fontSize: 13,
                    wordBreak: 'break-all',
                  }}
                >
                  {apiKeyRevealed
                    ? apiKey
                    : apiKey.length > 8
                      ? `${apiKey.slice(0, 4)}${'●'.repeat(Math.max(0, apiKey.length - 8))}${apiKey.slice(-4)}`
                      : '●'.repeat(apiKey.length)}
                </div>
                <Flex gap={8}>
                  <Button onClick={() => setApiKeyRevealed(!apiKeyRevealed)} disabled={isBusy}>
                    {apiKeyRevealed ? 'Masquer' : 'Afficher'}
                  </Button>
                  <Button onClick={handleCopyApiKey} disabled={isBusy}>
                    Copier
                  </Button>
                  <Button
                    type="primary"
                    danger
                    loading={apiKeyLoading}
                    disabled={isBusy}
                    onClick={() => setRevokeApiKeyModalVisible(true)}
                  >
                    Révoquer la clé
                  </Button>
                </Flex>
              </div>
            )}
          </div>
          <FormSectionTitle title={<Flex align="center"><ClockCircleOutlined style={{ marginRight: 6 }} />Dernière connexion</Flex>} />
          <FormContainer>
            <p
              style={{
                marginTop: '-16px',
                fontSize: '14px',
                color: wipUser.last_login === wipUser.created ? 'gray' : moment(wipUser.last_login).isBefore(
                  moment().subtract(1, 'month')
                )
                  ? 'red'
                  : 'green',
              }}
            >
              {wipUser.last_login === wipUser.created ? `Jamais connecté(e)` : `Dernière connexion ${moment(wipUser.last_login).fromNow()}`}
            </p>
          </FormContainer>

          <FormSectionTitle title={'📱 App Leedflow'} />
          <FormContainer>
            {(() => {
              const app = wipUser.appLeedflow;
              const device = app
                ? [
                  app.platform ? app.platform.charAt(0).toUpperCase() + app.platform.slice(1) : null,
                  app.os_version || null,
                ].filter(Boolean).join(' ') + (app.model ? `${app.platform || app.os_version ? ' - ' : ''}${app.model}` : '')
                : '';
              const version = app?.version
                ? `v${app.version}${app.build ? ` (${app.build})` : ''}${app.channel ? ` - ${app.channel}` : ''}`
                : '';
              const ota = app?.update_id ? `OTA ${app.update_id.slice(0, 8)}` : '';
              const seen = app?.last_seen ? `Vu ${moment(app.last_seen).fromNow()}` : '';

              if (!device && !version && !ota && !seen) {
                return <p style={{ marginTop: '-16px', fontSize: '14px', color: 'gray' }}>Aucune info disponible</p>;
              }

              return (
                <div style={{ marginTop: '-16px', fontSize: '14px' }}>
                  {device && <p style={{ margin: 0 }}>{device}</p>}
                  {version && <p style={{ margin: 0 }}>{version}</p>}
                  {ota && <p style={{ margin: 0 }} title={app?.update_id ?? undefined}>{ota}</p>}
                  {seen && <p style={{ margin: 0, color: 'gray' }}>{seen}</p>}
                </div>
              );
            })()}
          </FormContainer>

          <UserAppPermissions appLeedflow={wipUser.appLeedflow} />

          <FormSectionTitle title={'🏝️ Absences '} />
          <FormContainer>
            <Form.Item label="Date de début">
              <DatePicker
                value={wipUser.daysOffStart}
                style={{ width: '100%' }}
                onChange={(date) =>
                  userEditionFormContext.onDaysOffChange({ start: date || null })
                }
                format="DD/MM/YYYY"
                placeholder="Sélectionnez une date de début"
              />
            </Form.Item>

            <Form.Item label="Date de fin">
              <DatePicker
                value={wipUser.daysOffEnd}
                style={{ width: '100%' }}
                onChange={(date) =>
                  userEditionFormContext.onDaysOffChange({ end: date || null })
                }
                format="DD/MM/YYYY"
                placeholder="Sélectionnez une date de fin"
              />
            </Form.Item>

            <Form.Item label="Messagerie temporaire">
              <Input.TextArea
                value={wipUser.daysOffMessage}
                maxLength={200}
                showCount
                rows={3}
                placeholder="Message diffusé aux appelants pendant la période d'absence..."
                onChange={(e) =>
                  userEditionFormContext.onDaysOffChange({ message: e.target.value })
                }
              />
            </Form.Item>
          </FormContainer>

          <FormSectionTitle title={<Flex align="center"><MailOutlined style={{ marginRight: 6 }} />Emails</Flex>} />
          <FormContainer>
            <Form.Item label="Récapitulatif hebdo de l'activité">
              <Select
                style={{}}
                disabled={isBusy}
                onChange={(value) => {
                  userEditionFormContext.onEmailsActivitiesChange(
                    value as boolean
                  );
                }}
                value={wipUser.emails.activities}
              >
                <Select.Option value={true}>Oui</Select.Option>
                <Select.Option value={false}>Non</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Actualités et nouveautés de la plateforme">
              <Select
                style={{}}
                disabled={isBusy}
                onChange={(value) => {
                  userEditionFormContext.onEmailsNewsChange(value as boolean);
                }}
                value={wipUser.emails.news}
              >
                <Select.Option value={true}>Oui</Select.Option>
                <Select.Option value={false}>Non</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Intérêt des prospects">
              <Select
                style={{}}
                disabled={isBusy}
                onChange={(value) => {
                  userEditionFormContext.onEmailsProspectInterestChange(
                    value as boolean
                  );
                }}
                value={wipUser.emails.prospect_interest}
              >
                <Select.Option value={true}>Oui</Select.Option>
                <Select.Option value={false}>Non</Select.Option>
              </Select>
            </Form.Item>
          </FormContainer>

          <FormContainer>
            <Form.Item label="Envoyer un mail" >
              <Flex gap={8}>
                <Select
                  id='user-send-mail-section'
                  popupMatchSelectWidth={false}
                  options={Object.values(EMailType).map((type) => ({ label: mailTypeAsString(type), value: type }))}
                  onChange={(value) => {
                    setSelectedMailType(value as EMailType);
                  }}
                  value={selectedMailType}
                />

                <Button
                  type="primary"
                  loading={isBusy || isMailBusy}
                  disabled={isBusy || isMailBusy}
                  onClick={() => handlePreviewMail(selectedMailType)}
                >
                  Aperçu et envoi
                </Button>
              </Flex>
            </Form.Item>

            {mailTypeToHistoryEntryName(selectedMailType) ? (
              <Form.Item label="Dernier envoi">
                {lastSentMails?.[selectedMailType] ? moment(lastSentMails[selectedMailType]).format('DD/MM/YYYY à HH:mm') : 'Jamais'}
              </Form.Item>
            ) : null}
          </FormContainer>

          <FormSectionTitle title={<Flex align="center"><SwapOutlined style={{ marginRight: 6 }} />Transfert</Flex>} />
          <FormContainer>
            <Form.Item label="Destinataire">
              <Select
                style={{}}
                placeholder="Nom du nouveau conseiller attitré..."
                disabled={isBusy}
                onChange={(value) => {
                  setRecipientTransferSelected(value);
                }}
                value={recipientTransferSelected}
                options={subscriptionsContext.subscriptionUsers
                  .map((user) => {
                    return {
                      label: user.firstname + ' ' + user.lastname,
                      value: user.uid,
                    };
                  })
                  .filter((user) => user.value !== wipUser.uid)
                  .sort((a, b) => {
                    return a.label
                      .toLowerCase()
                      .localeCompare(b.label.toLowerCase());
                  })}
              />
            </Form.Item>

            <Form.Item label="Scénarios">
              <Select
                style={{}}
                allowClear
                placeholder="Tous les scénarios"
                mode="multiple"
                disabled={isBusy}
                onChange={(value) => {
                  setScenariosTransferSelected(value);
                }}
                value={scenariosTransferSelected}
                options={userEditionFormContext.scenarios.map((scenario) => {
                  return {
                    label: scenario.name,
                    value: scenario.id,
                  };
                })}
              />
            </Form.Item>
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <Form.Item label="">
                <Popconfirm
                  title="Confirmation"
                  description="Êtes-vous sûr de vouloir transférer les contacts ?"
                  okText="Oui"
                  cancelText="Non"
                  onConfirm={async () => {
                    if (recipientTransferSelected) {
                      await userEditionFormContext.handleContactsTransfer(
                        wipUser.uid,
                        recipientTransferSelected,
                        scenariosTransferSelected
                      );
                      window.location.reload();
                    }
                  }}
                >
                  <Button
                    type="primary"
                    icon={<ArrowRightOutlined />}
                    loading={isBusy}
                    disabled={isBusy || !recipientTransferSelected}
                  >
                    <span className="extra-label">Transférer</span>
                  </Button>
                </Popconfirm>
              </Form.Item>
            </div>
          </FormContainer>

          <FormSectionTitle title={<Flex align="center"><LockOutlined style={{ marginRight: 6 }} />Sécurité</Flex>} />
          <div style={{ display: 'flex', gap: '1rem' }}>
            {[UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
              authContext.authentication?.role ?? UserRole.guest
            ) && (
                <Form.Item label="">
                  <Button
                    type="primary"
                    onClick={() => {
                      setIsChangePasswordModalOpened(true);
                    }}
                    icon={<ToolOutlined />}
                    loading={isBusy}
                    disabled={isBusy}
                  >
                    <span className="extra-label">Changer le mot de passe</span>
                  </Button>
                </Form.Item>
              )}

            <Form.Item label="">
              <Button
                type="primary"
                onClick={async () => {
                  await userEditionFormContext.handleResetPassword();
                }}
                icon={<SyncOutlined />}
                loading={isBusy}
                disabled={isBusy}
              >
                <span className="extra-label">
                  Réinitialiser le mot de passe
                </span>
              </Button>
            </Form.Item>
            <Form.Item label="">
              <Button
                type="primary"
                danger
                onClick={async () => {
                  if (authContext.authentication) {
                    await revokeRefreshTokens({ userUid: wipUser.uid, auth: authContext.authentication });
                    message.success('Tokens révoqués avec succès');
                  }
                  else message.error('Utilisateur non authentifié');
                }}
                icon={<DeleteOutlined />}
                loading={isBusy}
                disabled={isBusy}
              >
                <span className="extra-label">Révoquer les tokens</span>
              </Button>
            </Form.Item>
          </div>
        </Form>
      </FormCard >
      {
        authContext.authentication?.role && [UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
          authContext.authentication.role
        ) && (
          <Modal
            title="Changer le mot de passe pour l'utilisateur "
            centered
            open={isChangePasswordModalOpened}
            onCancel={() => setIsChangePasswordModalOpened(false)}
            loading={isBusy}
            okText="Envoyer le mail à l'utilsateur"
            onOk={async () => {
              const success = await userEditionFormContext.submitChangePassword();
              console.log(success);
              if (success) {
                setIsChangePasswordModalOpened(false);
              }
            }}
          >
            <Form
              layout="vertical"
              style={{ marginTop: '2rem' }}
              initialValues={{
                wipChangePassword: '',
                wipChangePasswordConfirmation: '',
              }}
            >
              <Form.Item label="Nouveau mot de passe">
                <Input
                  type="password"
                  name="wipChangePassword"
                  value={userEditionFormContext.wipChangePassword}
                  onChange={(e) =>
                    userEditionFormContext.onWipChangePasswordChange(
                      e.target.value
                    )
                  }
                />
              </Form.Item>
              <Form.Item label="Confirmation du mot de passe">
                <Input
                  type="password"
                  name="wipChangePasswordConfirmation"
                  value={userEditionFormContext.wipChangePasswordConfirmation}
                  onChange={(e) =>
                    userEditionFormContext.onWipChangePasswordConfirmationChange(
                      e.target.value
                    )
                  }
                />
              </Form.Item>
              <Form.Item>
                <Checkbox
                  checked={userEditionFormContext.wipRequirePasswordChange}
                  onChange={(e) =>
                    userEditionFormContext.onWipRequirePasswordChangeChange(
                      e.target.checked
                    )
                  }
                >
                  Imposer un nouveau mot de passe à la prochaine connexion
                </Checkbox>
              </Form.Item>

              {userEditionFormContext.changePasswordErrorMessage && (
                <div>
                  <Typography.Text type="danger">
                    {userEditionFormContext.changePasswordErrorMessage}
                  </Typography.Text>
                </div>
              )}

              <Typography.Text type="secondary">
                <InfoCircleOutlined /> 8 caractères minimum
              </Typography.Text>
            </Form>
          </Modal>
        )
      }
      {emailFieldToEdit && (
        <ChangeUserEmailModal
          open={true}
          onClose={() => setEmailFieldToEdit(null)}
          userId={wipUser.uid}
          field={emailFieldToEdit}
          email={wipUser.email}
          emailAuthentication={wipUser.emailAuthentication}
          auth={authContext.authentication}
          onSuccess={() => window.location.reload()}
        />
      )}
      <Modal
        title={mailPreview?.subject}
        open={!!mailPreview}
        width={720}
        onCancel={() => setMailPreview(null)}
        footer={[
          <Button key="cancel" onClick={() => setMailPreview(null)} disabled={isMailBusy}>
            Annuler
          </Button>,
          <Button key="send" type="primary" loading={isMailBusy} onClick={handleConfirmSendMail}>
            Envoyer à {wipUser?.email}
          </Button>,
        ]}
      >
        <Flex vertical gap={12}>
          {[EMailType.WELCOME_LEEDPULSE, EMailType.ACCOUNT_ACTIVATED_LEEDFLOW, EMailType.MEMBER_WELCOME_LEEDFLOW].includes(selectedMailType) ? (
            <Alert showIcon type="warning" message="L'envoi réinitialise le mot de passe : le nouveau part dans le mail." />
          ) : null}
          <iframe
            title="Aperçu du mail"
            srcDoc={mailPreview?.html}
            sandbox=""
            style={{ width: '100%', height: 600, border: '1px solid #f0f0f0', borderRadius: 8 }}
          />
        </Flex>
      </Modal>

      < AvailabilityHoursModal
        open={isAvailabilityModalOpened}
        onClose={() => setIsAvailabilityModalOpened(false)}
        availabilityHours={wipUser?.availabilityHours ?? null}
      />
      < Modal
        title="Révoquer la clé API"
        open={revokeApiKeyModalVisible}
        onCancel={() => setRevokeApiKeyModalVisible(false)}
        footer={
          [
            <Button key="cancel" onClick={() => setRevokeApiKeyModalVisible(false)}>
              Annuler
            </Button>,
            <Button
              key="submit"
              type="primary"
              danger
              loading={apiKeyLoading}
              onClick={handleRevokeApiKey}
            >
              Révoquer
            </Button>,
          ]}
      >
        <p>
          Êtes-vous sûr de vouloir révoquer la clé API de cet utilisateur ? Toutes les
          intégrations utilisant cette clé cesseront de fonctionner immédiatement.
        </p>
      </Modal >
    </>
  );
};

export default UserDetails;
